import * as React from 'react';
import { BrowserRouter, NavLink, Route, Routes } from 'react-router-dom';
import AdminWorkspace from './AdminWorkspace';
import AnalyticsWorkspace from './AnalyticsWorkspace';
import PlannerWorkspace from './PlannerWorkspace';
import { PlannerActionProvider, usePlannerActions } from './PlannerActionContext';
import { api } from './api';
import { createBudgetDraft } from './plannerDefaults';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';

function CreateBudgetDialog({ open, onClose, budgets, refreshBudgets, setSelectedYear }) {
  const [newYear, setNewYear] = React.useState(new Date().getFullYear() + 1);
  const [newComment, setNewComment] = React.useState('');
  const [copySourceBudgetId, setCopySourceBudgetId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  if (!open) return null;

  async function createBudgetForYear() {
    try {
      setSaving(true);
      setError('');
      const targetYear = Number(newYear);
      let draft;
      if (copySourceBudgetId) {
        const source = await api.getPlannerBudget(copySourceBudgetId);
        draft = {
          id: 0,
          year: targetYear,
          currency_code: source.budget.currency_code || 'RUB',
          notes: newComment || `Черновик ${targetYear} скопирован из бюджета ${source.budget.year}.`,
          sync_config: source.sync_config,
          items: source.items
            .filter((item) => (item.name || '').trim())
            .map((item, idx) => ({
              ...item,
              id: 0,
              budget_id: 0,
              sort_order: idx + 1,
              firefly_budget_id: null,
              firefly_bill_id: null,
              firefly_rule_ids: [],
              last_synced_at: null,
              last_sync_status: '',
              last_sync_summary: '',
            })),
        };
      } else {
        const baseDraft = createBudgetDraft(targetYear);
        draft = {
          id: 0,
          year: targetYear,
          currency_code: 'RUB',
          notes: newComment || baseDraft.budget.notes,
          sync_config: baseDraft.sync_config,
          items: baseDraft.items.filter((item) => (item.name || '').trim()),
        };
      }
      await api.savePlannerBudget(draft);
      await refreshBudgets();
      setSelectedYear(targetYear);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-panel" onClick={(e) => e.stopPropagation()}>
        <h2>Создать бюджет</h2>
        {error && <div className="message-banner error">{error}</div>}
        <label className="field">
          <span>Год</span>
          <input type="number" value={newYear} onChange={(e) => setNewYear(Number(e.target.value))} />
        </label>
        <label className="field">
          <span>Комментарий</span>
          <textarea rows={3} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Комментарий к бюджету" />
        </label>
        <label className="field">
          <span>Копировать из</span>
          <select value={copySourceBudgetId} onChange={(e) => setCopySourceBudgetId(e.target.value)}>
            <option value="">Пустой черновик</option>
            {budgets.map((budget) => (
              <option key={budget.id} value={budget.id}>
                Бюджет {budget.year}
              </option>
            ))}
          </select>
        </label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onClose}>Отмена</button>
          <button type="button" className="primary-button" onClick={createBudgetForYear} disabled={saving}>
            {saving ? 'Создаю...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AppNavigation() {
  const {
    saveBudget, syncBudget, getFireflyData,
    saving, syncing, loadingYear, selectedBudgetId,
    selectedYear, setSelectedYear, budgets, refreshBudgets,
  } = usePlannerActions();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);

  return (
    <>
      <CreateBudgetDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        budgets={budgets}
        refreshBudgets={refreshBudgets}
        setSelectedYear={setSelectedYear}
      />
      <nav className="top-nav">
        <div className="brand-block">
          <p className="brand-kicker">FBP</p>
          <span className="brand-title">Firefly-iii Budget Planner</span>
        </div>
        <div className="nav-links">
          <NavLink to="/" end className="nav-link">
            Планирование
          </NavLink>
          <NavLink to="/analytics" className="nav-link">
            Аналитика
          </NavLink>
          <NavLink to="/admin" className="nav-link">
            Администрирование
          </NavLink>
        </div>
        <div className="nav-actions">
          <button type="button" className="ghost-button nav-action-button" onClick={() => setIsCreateDialogOpen(true)}>
            <AddIcon fontSize="small" /> Создать
          </button>
          <label className="field compact-field nav-year-field">
            <select
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.year}>
                  {budget.year}
                </option>
              ))}
              {!budgets.find((b) => b.year === selectedYear) && (
                <option value={selectedYear}>{selectedYear} (нет бюджета)</option>
              )}
            </select>
          </label>
          <button type="button" className="ghost-button nav-action-button" onClick={getFireflyData} disabled={!selectedBudgetId || loadingYear}>
            <SyncIcon fontSize="small" /> {loadingYear ? '...' : 'Обновить данные Firefly'}
          </button>
          <button type="button" className="primary-button nav-action-button" onClick={saveBudget} disabled={!saveBudget || saving}>
            <SaveOutlinedIcon fontSize="small" /> {saving ? '...' : 'Сохранить'}
          </button>
          <button type="button" className="ghost-button nav-action-button" onClick={syncBudget} disabled={!syncBudget || syncing}>
            <SyncIcon fontSize="small" /> {syncing ? '...' : 'Синхронизировать'}
          </button>
        </div>
      </nav>
    </>
  );
}

function AppFrame({ children }) {
  return (
    <>
      <AppNavigation />
      {children}
    </>
  );
}

export default function App() {
  const [authChecked, setAuthChecked] = React.useState(false);

  React.useEffect(() => {
    api.authStatus().then(() => {
      setAuthChecked(true);
    }).catch(() => {
      window.location.href = '/api/auth/login?redirect=' + encodeURIComponent(window.location.href);
    });
  }, []);

  if (!authChecked) {
    return (
      <div className="loading-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <PlannerActionProvider>
      <BrowserRouter basename="/app">
        <Routes>
          <Route
            path="/"
            element={
              <AppFrame>
                <PlannerWorkspace />
              </AppFrame>
            }
          />
          <Route
            path="/admin"
            element={
              <AppFrame>
                <AdminWorkspace />
              </AppFrame>
            }
          />
          <Route
            path="/analytics"
            element={
              <AppFrame>
                <AnalyticsWorkspace />
              </AppFrame>
            }
          />
        </Routes>
      </BrowserRouter>
    </PlannerActionProvider>
  );
}
